import { CmgUiUtilities } from '../components/common/cmg-ui-utils';
import { EmploymentStatus } from './lookups/employment.lookups';
import { PropertyTaxCalculation } from './lookups/expenses.lookups';

export class EmploymentIncomeExpenseModel {
  applicationId?: number;
  channelId?: number;
  organizationId?: number;
  /**
   * borrowerDefaultEmploymentId keeps track of default record
   * that byte creates when a borrower is created.
   */
  borrowerDefaultEmploymentId?: number;
  borrowerEmployers?: EmploymentModel[];
  /**
   *   coBorrowerDefaultEmploymentId keeps track of default record of type primary
   * that byte creates when a co-borrower is created.
   */
  coBorrowerDefaultEmploymentId?: number;
  coBorrowerEmployers?: EmploymentModel[];

  /** Contains incomes for BOTH the primary AND additional borrower (if present). */
  grossMonthlyIncomes?: IncomeModel[];

  combinedHousingExpenses?: CombinedHousingExpenseModel;
  otherExpenses?: OtherExpenseModel;
}

export class EmploymentModel {
  static createEmpty() {
    //TODO: fill in defaults
    const model = new EmploymentModel();
    model.employmentId = 0;
    model.employmentType = EmploymentStatus.SecondaryCurrent;
    model.isSpecialRelationship = false;
    model.country = 'US';
    model.yearsInJob = 0;
    model.monthsInJob = 0;
    model.isDeleted = false;
    model.monthlyIncome = 0;
    model.ownershipInterest = 0;
    return model;
  }

  borrowerId?: number;
  businessPhone?: string;
  city?: string;
  country?: string;
  dateFrom?: Date | string;
  dateTo?: Date | string;
  employmentId?: number;
  employerName?: string;
  employmentType?: EmploymentStatus;
  isDeleted?: boolean;
  isSelfEmployment?: boolean;
  isSpecialRelationship: boolean;
  monthlyIncome?: number;
  yearsInJob?: number; // calculated from to/from data
  monthsInJob?: number; // calculated from to/from data
  yearsInLineOfWork?: number; // Line of Work (not calculated)
  monthsInLineOfWork?: number; // Line of Work (not calculated)
  ownershipInterest?: number;
  position?: string;
  state?: string;
  street?: string;
  zip?: string;
  notes?: string;
  aptSuiteOther?: string;
  displayOrder?: number;
  voeEmployerId?: string;
  guid?: string;
  companyLogoUrl?: string;

  constructor() {
    this.borrowerId = 0;
    this.businessPhone = null;
    this.city = null;
    this.country = 'US';
    this.dateFrom = null;
    this.dateTo = null;
    this.employmentId = 0;
    this.employerName = null;
    this.employmentType = EmploymentStatus.SecondaryCurrent;
    this.isDeleted = false;
    this.isSelfEmployment = false;
    this.isSpecialRelationship = false;
    this.monthlyIncome = 0;
    this.yearsInJob = 0;
    this.monthsInJob = 0;
    this.yearsInLineOfWork = null;
    this.monthsInLineOfWork = null;
    this.ownershipInterest = 0;
    this.position = null;
    this.state = null;
    this.street = null;
    this.zip = null;
    this.notes = null;
    this.aptSuiteOther = null;
    this.displayOrder = null;
  }
}

export class IncomeModel {
  amount?: number;
  borrowerId?: number;
  displayOrder?: number;

  /** If null/undefined --> Becomes "Other" income on UI. */
  employmentId?: number;

  hoursPerWeek?: number;
  incomeFrequencyType?: number;
  incomeId?: number;
  incomeRate?: number;

  /** Enum: IncomeType */
  incomeType?: number;

  /** Variable Income Properties */
  variablePeriod1Desc?: string;
  variablePeriod1Income?: number;
  variablePeriod1Months?: number;
  variablePeriod2Desc?: string;
  variablePeriod2Income?: number;
  variablePeriod2Months?: number;
  variablePeriod3Desc?: string;
  variablePeriod3Income?: number;
  variablePeriod3Months?: number;

  /** Notes */
  qmatrNotes?: string;
  notes?: string;

  isDeleted?: boolean;
  static createEmpty(borrowerId: number, employmentId: number = undefined, incomeType = null) {
    const incomeModel = new IncomeModel();
    incomeModel.incomeId = -CmgUiUtilities.getUniqueNumber();
    incomeModel.borrowerId = borrowerId;
    incomeModel.employmentId = employmentId;
    incomeModel.incomeType = incomeType; /* Select/empty placeholder */
    incomeModel.amount = null;
    incomeModel.isDeleted = false;
    return incomeModel;
  }
}

export interface PropertyTaxCalculationType {
  id: number;
  value: PropertyTaxCalculation;
}

// [Note]: Subordinate Lien data is in LoanDataModel.
export interface CombinedHousingExpenseModel {
  isIncludedInPiti?: boolean;
  presentRent?: number;
  firstMortgage?: HousingExpenseDetailModel;
  hazardInsurance?: HousingExpenseDetailModel;
  homeOwnerAssnDues?: HousingExpenseDetailModel;
  mortgageInsurance?: HousingExpenseDetailModel;
  otherHousingExpenses?: HousingExpenseModel[];
  otherMortgage?: HousingExpenseDetailModel;
  othersTotal?: HousingExpenseDetailModel;
  propertyInsurance?: HousingExpenseDetailModel;
  realEstateTaxes?: HousingExpenseDetailModel;
  total?: HousingExpenseDetailModel;
  propertyTaxCalculationType: PropertyTaxCalculationType | PropertyTaxCalculation;
}

export class HousingExpenseDetailModel {
  presentAmount?: number;
  factor?: number;

  /** Enum: PrepaidItemType */
  expenseType?: number;
  proposedAmount?: number;
  prepaidItemId?: number;
  premiumPaidToType?: number;
  isDeleted?: boolean;
}

export interface HousingExpenseModel {
  /** Enum: PrepaidItemType */
  expenseType?: number;
  proposedAmount?: number;
  prepaidItemId?: number;
  premiumPaidToType?: number;
  isDeleted?: boolean;
}

export interface OtherExpenseModel {
  /** Enum: A subset of PrepaidItemType? */
  expenseType?: number;
  amount?: number;
}

export enum PropertyTaxUpdateType {
  SalesPrice = 0,
  Factor = 1,
  Amount = 2,
}
